// completemgr.ts ↔ game/completemanager.cpp — the gpcptm mission-completion
// tracker. The C++ persists one zero-length file per completed mission in the
// completes dir, named "kkkkkkkk-hhhh...h" (key-hash); the directory becomes a
// CompleteStore seam (filenames in, filenames out — IndexedDB/in-memory).
//
// The WEB build runs MarkComplete on every SP win (Player.cpp:1157,
// game.cpp:1706) but compiles the mission-list IsComplete/LOCKED display OUT
// (#if !defined(WEB), selectmission.cpp:193-216) — IsComplete remains live for
// the MP create-game gate (creategameform.cpp:244, M5).

import type { PackFileReader } from './packfile.ts';
import type { MissionIdentifier, PackId } from './savegame.ts';

/** The completes-directory seam (opendir/readdir + fopen(...,"w")). */
export interface CompleteStore {
  list(): string[]; // readdir
  create(filename: string): boolean; // fopen(filepath, "w") — zero-length file
}

/** The default in-memory store (IndexedDB lands with the browser shell). */
export class MemoryCompleteStore implements CompleteStore {
  private files = new Set<string>();
  list(): string[] {
    return [...this.files];
  }
  create(filename: string): boolean {
    this.files.add(filename);
    return true;
  }
}

/** gppackm->Mount/Unmount around the pack hash (httppackmanager.cpp — M5 DLC;
 *  the main pack is permanently mounted, so the M4 default is a no-op). */
export interface PackMountSeam {
  Mount?(pakr: PackFileReader, ppackid: PackId): void;
  Unmount?(pakr: PackFileReader, ppackid: PackId): void;
}

const toHex = (b: Uint8Array): string =>
  [...b].map((x) => x.toString(16).padStart(2, '0')).join('');

export class CompleteManager {
  private readonly store: CompleteStore;
  private readonly pakr: PackFileReader;
  private readonly packm: PackMountSeam;
  // typedef std::multimap<dword, MissionHash> CompleteMap
  private map_ = new Map<number, Uint8Array[]>();

  constructor(store: CompleteStore, pakr: PackFileReader, packm: PackMountSeam = {}) {
    this.store = store;
    this.pakr = pakr;
    this.packm = packm;
  }

  // game/completemanager.cpp:11-24
  Init(): void {
    this.map_.clear();
    for (const filename of this.store.list()) {
      const parsed = this.ParseFilename(filename);
      if (parsed === null) continue;
      const list = this.map_.get(parsed.key);
      if (list === undefined) this.map_.set(parsed.key, [parsed.hash]);
      else list.push(parsed.hash);
    }
  }

  // game/completemanager.cpp:26-46 — "kkkkkkkk-hhhhhhhh...h" (8 hex key, '-',
  // 32 hex md5)
  ParseFilename(filename: string): { key: number; hash: Uint8Array } | null {
    if (filename.length !== 8 + 1 + 32) return null;
    const szId = filename.slice(0, 8);
    if (!/^[0-9a-fA-F]{8}$/.test(szId)) return null; // base::Format::ToDword(s, 16)
    const key = parseInt(szId, 16) >>> 0;
    const szHash = filename.slice(9);
    if (!/^[0-9a-fA-F]{32}$/.test(szHash)) return null; // base::Format::FromHex
    const hash = new Uint8Array(16);
    for (let i = 0; i < 16; i++) hash[i] = parseInt(szHash.slice(i * 2, i * 2 + 2), 16);
    return { key, hash };
  }

  // game/completemanager.cpp:44-48
  GetFilename(key: number, hash: Uint8Array): string {
    // base::Format::ToString("%08x-%s", key, szHash)
    return `${(key >>> 0).toString(16).padStart(8, '0')}-${toHex(hash)}`;
  }

  // game/completemanager.cpp:50-74
  IsComplete(pmiid: MissionIdentifier): boolean {
    // Quickly fail
    const key = this.GetKey(pmiid);
    const list = this.map_.get(key);
    if (list === undefined || list.length === 0) return false;

    // Slow verification
    const mh = this.GetMissionHash(pmiid);
    if (mh === null) return false;

    // See if this hash is known.
    // Using a multimap for backwards compat reasons. See BadHashString.
    for (const hash of list) {
      if (hash.length === mh.length && hash.every((b, i) => b === mh[i])) {
        return true;
      }
    }

    return false;
  }

  // game/completemanager.cpp:76-85
  GetMissionHash(pmiid: MissionIdentifier): Uint8Array | null {
    this.packm.Mount?.(this.pakr, pmiid.packid);
    const hash = this.pakr.HashFile(pmiid.szLvlFilename);
    this.packm.Unmount?.(this.pakr, pmiid.packid);
    return hash;
  }

  // game/completemanager.cpp:87-90
  GetKey(pmiid: MissionIdentifier): number {
    const s = (pmiid.packid.id >>> 0).toString(16).padStart(8, '0'); // "%08x"
    return (this.BadHashString(s) ^ this.BadHashString(pmiid.szLvlFilename)) >>> 0;
  }

  // game/completemanager.cpp:92-113
  MarkComplete(pmiid: MissionIdentifier): void {
    const mh = this.GetMissionHash(pmiid);
    if (mh === null) return;
    const key = this.GetKey(pmiid);
    const filename = this.GetFilename(key, mh);

    // This will create a zero length file. Perhaps in the future stats can
    // be stored in this file, and this will become StatManager instead.

    if (this.store.create(filename)) {
      const list = this.map_.get(key);
      if (list === undefined) this.map_.set(key, [mh]);
      else list.push(mh);
    }
  }

  // game/completemanager.cpp:114-129
  BadHashString(s: string): number {
    // This string hash is here for a few reasons:
    // 1. WI v1.5 CompleteManager uses this hash algorithm, and has
    //    committed its values to storage (oops)
    // 2. This used to be the default string hash algorithm. It is no longer,
    //    because it is broken. For example, s_05.lvl hashes to the same value
    //    as s_10.lvl (as well as 6 to 11, 7 to 12, 8 to 13, and 9 to 14).
    // 3. The easiest thing to do is to continue using it, and use a multimap
    //    for lookup, instead of a map (which is now the case).
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(5, h) + s.charCodeAt(i)) >>> 0;
    }
    return h;
  }
}
